/**
 * The teacher's shared standards default: the state, subject and grades every Labs tool
 * prefills from and writes back to. Pure.
 *
 * A `Profile` has all three fields or it is not a profile, because analysis cannot run on
 * less. The default is the same three fields, each optional, because a pick in Planner or
 * Collab carries one grade and maybe no subject, and that pick must still count. The values
 * are the same Learning Commons enums as `core/profile.ts`, so a complete default is a
 * Profile with nothing converted.
 *
 * Two rules decide every write, wherever it came from:
 *
 * - A field the pick carries replaces the stored field. Grades replace the whole set, so a
 *   single grade collapses a multi-grade default to that grade. The user chose the literal
 *   last pick over keeping or accumulating the set.
 * - A field the pick does not carry keeps the stored value. `null`, `''`, `[]` and any
 *   value outside the enums count as not carried, so no tool can erase a field by sending
 *   an empty form.
 *
 * The worker's UPSERT applies the same rules in SQL so concurrent partial writes cannot
 * drop each other's fields; this module is the statement of them and the source of what a
 * write reports.
 */

import {
  ACADEMIC_SUBJECTS,
  GRADE_LEVELS,
  JURISDICTIONS,
  type AcademicSubject,
  type GradeLevel,
  type Jurisdiction,
  isPlainObject,
  type Profile,
} from './profile';

export interface StandardsDefault {
  jurisdiction?: Jurisdiction;
  academicSubject?: AcademicSubject;
  gradeLevels?: GradeLevel[];
}

export interface StoredStandardsDefault extends StandardsDefault {
  updatedAt: string;
  /** Which tool wrote last. Null on rows from before the default was shared. */
  updatedByTool: string | null;
}

export const STANDARDS_FIELDS = ['jurisdiction', 'academicSubject', 'gradeLevels'] as const;

export type StandardsField = (typeof STANDARDS_FIELDS)[number];

export interface StandardsMerge {
  next: StoredStandardsDefault;
  /** The fields this write replaced, in a fixed order. Never values. */
  fields: StandardsField[];
  /** The stored set had more than one grade and the result has exactly one. */
  gradesCollapsed: boolean;
}

/**
 * The fields of `raw` that are real values, and nothing else. Never refuses: a pick with
 * nothing usable is an empty pick, which the merge reads as "change nothing".
 *
 * Unknown grades are dropped one by one and the known ones kept, in enum order and
 * de-duplicated, the same normal form `decideProfile` writes.
 */
export function decideStandardsPick(raw: unknown): StandardsDefault {
  if (!isPlainObject(raw)) return {};
  const pick: StandardsDefault = {};

  if (JURISDICTIONS.includes(raw.jurisdiction as Jurisdiction)) {
    pick.jurisdiction = raw.jurisdiction as Jurisdiction;
  }
  if (ACADEMIC_SUBJECTS.includes(raw.academicSubject as AcademicSubject)) {
    pick.academicSubject = raw.academicSubject as AcademicSubject;
  }
  if (Array.isArray(raw.gradeLevels)) {
    const given = raw.gradeLevels as unknown[];
    const grades = GRADE_LEVELS.filter((g) => given.includes(g));
    if (grades.length > 0) pick.gradeLevels = [...grades];
  }
  return pick;
}

/** Apply `pick` to `stored` (null when the teacher has no row yet). */
export function mergeStandardsDefault(
  stored: StandardsDefault | null,
  pick: StandardsDefault,
  now: string,
  tool: LabsTool,
): StandardsMerge {
  const next: StoredStandardsDefault = { updatedAt: now, updatedByTool: tool };
  const jurisdiction = pick.jurisdiction ?? stored?.jurisdiction;
  const academicSubject = pick.academicSubject ?? stored?.academicSubject;
  const gradeLevels = pick.gradeLevels ?? stored?.gradeLevels;
  if (jurisdiction !== undefined) next.jurisdiction = jurisdiction;
  if (academicSubject !== undefined) next.academicSubject = academicSubject;
  if (gradeLevels !== undefined) next.gradeLevels = [...gradeLevels];

  const fields = STANDARDS_FIELDS.filter((field) => pick[field] !== undefined);
  const gradesCollapsed = pick.gradeLevels !== undefined
    && (stored?.gradeLevels?.length ?? 0) > 1
    && pick.gradeLevels.length === 1;

  return { next, fields, gradesCollapsed };
}

/** The default as a Profile when all three fields are present, else null. */
export function completeProfile(value: StandardsDefault): Profile | null {
  const { jurisdiction, academicSubject, gradeLevels } = value;
  if (!jurisdiction || !academicSubject || !gradeLevels?.length) return null;
  return { jurisdiction, academicSubject, gradeLevels: [...gradeLevels] };
}

/** True when the default carries no field at all. */
export function isEmptyDefault(value: StandardsDefault): boolean {
  return STANDARDS_FIELDS.every((field) => value[field] === undefined);
}

/**
 * The Labs tools that may read and write the default. The name is stamped on the row as
 * `updated_by_tool` and on every event, so an unknown one is refused rather than stored.
 */
export const LABS_TOOLS = ['objectives', 'osa', 'planner', 'collab'] as const;

export type LabsTool = (typeof LABS_TOOLS)[number];

/** The `tool` query parameter as a known tool, or null. Exact match: no trimming, no case folding. */
export function decideTool(raw: string | null): LabsTool | null {
  return LABS_TOOLS.includes(raw as LabsTool) ? (raw as LabsTool) : null;
}

export type StandardsPatchDecision =
  | { ok: true; pick: StandardsDefault; matchedPrefill: boolean | null }
  | { ok: false };

/**
 * A PATCH body. Only a body that is not an object is refused; within one, every field is
 * lenient (`decideStandardsPick`). `matchedPrefill` is the caller's word that the submitted
 * values equal what its form prefilled, and null when it did not say.
 */
export function decideStandardsPatch(raw: unknown): StandardsPatchDecision {
  if (!isPlainObject(raw)) return { ok: false };
  const matchedPrefill = typeof raw.matchedPrefill === 'boolean' ? raw.matchedPrefill : null;
  return { ok: true, pick: decideStandardsPick(raw), matchedPrefill };
}
